/*
  V_PV_PDP_08 | PDP "Us vs Them" comparison table — winner (Var A)

  Fills the [generic] / [luxury] amount slots Liquid could not: every currency
  other than AUD, the store currency the amounts are set in. Liquid has no
  exchange rate, so the slot carries the AUD amount and this multiplies it by
  Shopify's storefront rate — the same rate the
  visitor's product prices were converted with — then formats it from the
  money sample Liquid rendered in the visitor's currency.

  Every value comes from the markup (data-c-pdp08-*), so nothing here is a
  setting of its own. A slot that cannot be filled is left empty, never shown in
  the wrong currency.

  The guard keeps a second copy of this file (the theme editor re-rendering
  the section) from running twice.
*/
(() => {
  if (window.cPdp08Ready) return;
  window.cPdp08Ready = true;

  // "$1,000,000 AUD" / "1.000.000 €" / "£1,000,000": everything around the
  // number is kept, and the separator after the leading 1 is the thousands one.
  function cPdp08Format(amount, sample) {
    const match = /1(\D?)000\1(?:000)/.exec(sample);
    if (!match) return null;
    const digits = String(amount).replace(/\B(?=(\d{3})+(?!\d))/g, match[1]);
    return sample.slice(0, match.index) + digits + sample.slice(match.index + match[0].length);
  }

  function cPdp08Fill(root) {
    const slots = root.querySelectorAll("[data-c-pdp08-amount]:empty");
    if (!slots.length) return;

    // Only trust the rate when Shopify's active currency is the one Liquid
    // rendered the sample for, so a mismatched page can never mix the two.
    const currency = window.Shopify?.currency;
    const rate = parseFloat(currency?.rate);
    if (!rate || currency.active !== root.dataset.cPdp08Currency) return;

    const step = parseFloat(root.dataset.cPdp08Round) || 1;
    const sample = root.dataset.cPdp08MoneySample || "";

    slots.forEach((slot) => {
      const base = parseFloat(slot.dataset.cPdp08Amount);
      if (!base) return;
      // Two significant figures, never finer than the step setting: €14.62
      // reads €15, ¥75,213 reads ¥75,000 rather than ¥75,215.
      const raw = base * rate;
      const unit = Math.max(step, 10 ** (Math.floor(Math.log10(raw)) - 1));
      const converted = Math.max(unit, Math.round(raw / unit) * unit);
      const text = cPdp08Format(converted, sample);
      if (text) slot.textContent = text + (slot.dataset.cPdp08Suffix || "");
    });
  }

  function cPdp08Init(scope) {
    scope.querySelectorAll(".c-pdp08").forEach(cPdp08Fill);
  }

  // The Pelvini amount in one market's currency (Theme settings, US / USD),
  // whatever the visitor's own currency. Liquid only knows the visitor's
  // price, so the watch's price in that market comes from Shopify's storefront
  // API, once per product per page however many tables render it. The BOGO
  // maths is the snippet's: price x multiplier / quantity, to the cent.
  const cPdp08MarketPrices = {};

  function cPdp08MarketPrice(root) {
    const handle = root.dataset.cPdp08Handle;
    const variant = root.dataset.cPdp08Variant;
    const priceCountry = root.dataset.cPdp08PriceCountry;
    const priceCurrency = root.dataset.cPdp08PriceCurrency;
    const key = handle + "|" + priceCountry;
    if (!cPdp08MarketPrices[key]) {
      cPdp08MarketPrices[key] = fetch("/api/2025-01/graphql.json", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          query: "query ($handle: String!, $country: CountryCode!) @inContext(country: $country) { product(handle: $handle) { variants(first: 250) { nodes { id price { amount currencyCode } } } } }",
          variables: { handle: handle, country: priceCountry }
        })
      })
        .then((response) => response.json())
        .then((result) => {
          const nodes = result?.data?.product?.variants?.nodes || [];
          const match = nodes.find((node) => node.id.split("/").pop() === variant) || nodes[0];
          if (!match || match.price.currencyCode !== priceCurrency) throw new Error("no " + priceCurrency + " price");
          return Math.round(parseFloat(match.price.amount) * 100);
        });
    }
    return cPdp08MarketPrices[key];
  }

  function cPdp08FillMarketPrices(scope) {
    scope.querySelectorAll(".c-pdp08").forEach((root) => {
      const slots = root.querySelectorAll("[data-c-pdp08-market-price]:empty");
      if (!slots.length) return;
      const quantity = parseFloat(root.dataset.cPdp08BogoQuantity) || 1;
      const multiplier = parseFloat(root.dataset.cPdp08BogoMultiplier) || 1;
      cPdp08MarketPrice(root)
        .then((cents) => {
          const unit = Math.round((cents * multiplier) / quantity) / 100;
          const text = new Intl.NumberFormat("en-" + root.dataset.cPdp08PriceCountry, {
            style: "currency",
            currency: root.dataset.cPdp08PriceCurrency
          }).format(unit);
          slots.forEach((slot) => { slot.textContent = text; });
        })
        .catch(() => {
          // Never an empty cell: the visitor's own price, as Liquid had it.
          slots.forEach((slot) => { slot.textContent = slot.dataset.cPdp08Fallback; });
        });
    });
  }

  function cPdp08Start() {
    cPdp08Init(document);
    cPdp08FillMarketPrices(document);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", cPdp08Start);
  } else {
    cPdp08Start();
  }

  // Theme editor re-renders the section in place.
  document.addEventListener("shopify:section:load", (event) => {
    cPdp08Init(event.target);
    cPdp08FillMarketPrices(event.target);
  });
})();
